import useAuth from "../../../hooks/useAuth";
import useDeceased from "../../../hooks/useDeceased";
import {useCallback, useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {updateEventThoutght} from "../../../api/pro/eventReq";
import {
    capitalize,
    getErrorListFromValidation,
    removeErrorList
} from "../../../helper/tools";

// "Marié(e)" -> conjoint, "Pacsé(e)"/"Union libre" -> compagnon/compagne (cf. maritalOptions
// dans FormFamilyStatusInfos.jsx pour la correspondance des codes).
const getDefaultThoughtsTo = (deceasedInfos, childrenInfos, partnerInfos) => {
    if (!deceasedInfos) return "";

    // Le genre du terme employé pour le/la conjoint(e) dépend de la civilité du conjoint
    // lui-même (mari = conjoint masculin, femme = conjoint féminin), pas de celle du défunt :
    // on ne suppose donc un genre que si la civilité du conjoint est réellement renseignée.
    const partnerIsFeminine = partnerInfos?.civility?.toUpperCase() === "MME";
    const partnerCivilityKnown = !!partnerInfos?.civility;
    let spouseTerm = "";
    if (deceasedInfos?.maritalStatus == 1) {
        spouseTerm = partnerCivilityKnown ? (partnerIsFeminine ? "sa femme" : "son mari") : "son/sa conjoint(e)";
    } else if (deceasedInfos?.maritalStatus == 5 || deceasedInfos?.maritalStatus == 6) {
        spouseTerm = partnerCivilityKnown ? (partnerIsFeminine ? "sa compagne" : "son compagnon") : "son/sa compagnon/compagne";
    }
    if (spouseTerm && partnerInfos?.firstName) {
        spouseTerm = `${spouseTerm} ${capitalize(partnerInfos.firstName)}`;
    }

    // Un enfant déjà décédé ne peut pas recevoir de soutien : on l'exclut des destinataires.
    const children = childrenInfos?.filter(child => child?.firstName && !child?.isDeceased) || [];
    let childrenPhrase = "";
    if (children.length === 1) {
        // Un seul enfant : "son fils"/"sa fille" plutôt que "ses enfants".
        const childIsFeminine = children[0]?.civility?.toUpperCase() === "MME";
        childrenPhrase = `${childIsFeminine ? "sa fille" : "son fils"} ${capitalize(children[0].firstName)}`;
    } else if (children.length > 1) {
        const names = children.map(child => capitalize(child.firstName));
        childrenPhrase = `ses enfants ${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}`;
    }

    const recipient = (spouseTerm && childrenPhrase)
        ? `${spouseTerm} et ${childrenPhrase}`
        : spouseTerm || childrenPhrase;

    return recipient ? `Tout notre soutien à ${recipient}` : "";
};

export default function FormAddedInfos({
    apiEventAddedInfos,
    apiDeceasedInfos,
    thoughts,
    registerSubmitFunction,
    noSubmitButton = false
}) {
    const { auth } = useAuth();
    const { deceasedInfos, childrenInfos, partnerInfos } = useDeceased();
    const token = auth?.token;
    const { id } = useParams();
    const fieldsetRef = useRef();
    const hasSavedValueRef = useRef(false);
    // Dernière valeur générée automatiquement : tant que le champ correspond encore à cette
    // valeur (pas d'édition manuelle), on peut la régénérer si le conjoint/les enfants changent.
    const lastAutoFilledRef = useRef("");
    const [ thoughtsTo, setThoughtsTo ] = useState(thoughts || "");
    const [isFormModified, setIsFormModified] = useState(false);

    // Si ce composant reste monté en changeant de défunt (ex: navigation dans le wizard sans
    // démontage), son state ne se réinitialisait pas tout seul : un défunt sans message
    // personnalisé encore enregistré (thoughts vide) affichait donc le texte du défunt précédent,
    // faute d'effet déclenché sur un `id` vide/falsy.
    useEffect(() => {
        setThoughtsTo("");
        hasSavedValueRef.current = false;
        lastAutoFilledRef.current = "";
    }, [id]);

    useEffect(() => {
        if (thoughts) {
            setThoughtsTo(thoughts);
            if (thoughts.startsWith("Tout notre soutien à ")) {
                // Valeur probablement auto-générée (même par une ancienne version du calcul) :
                // on la laisse encore régénérable pour corriger silencieusement un calcul obsolète.
                lastAutoFilledRef.current = thoughts;
            } else {
                // Message manifestement personnalisé par l'utilisateur : on ne le touche plus.
                hasSavedValueRef.current = true;
            }
        }
    }, [thoughts]);

    // Préremplit "Tout notre soutien à" avec le conjoint/compagnon et les enfants du défunt, et
    // continue de le régénérer si le conjoint/les enfants changent, tant que l'utilisateur n'a
    // pas édité le champ lui-même et qu'aucune valeur n'a déjà été enregistrée.
    useEffect(() => {
        if (hasSavedValueRef.current || (thoughtsTo && thoughtsTo !== lastAutoFilledRef.current)) {
            return;
        }
        // deceasedInfos/childrenInfos/partnerInfos viennent d'un contexte partagé rechargé par un
        // composant parent : juste après un changement de défunt, ils peuvent encore correspondre
        // brièvement à l'ANCIEN défunt tant que le fetch du nouveau n'a pas abouti. Les utiliser
        // tels quels régénérait le texte avec l'enfant/conjoint du défunt précédent. On attend donc
        // que deceasedInfos corresponde bien à l'id courant, et on ignore les enfants dont
        // deceasedId ne correspond pas encore.
        if (!deceasedInfos || String(deceasedInfos?.id) !== String(id)) {
            return;
        }
        const currentChildrenInfos = childrenInfos?.filter(child => String(child?.deceasedId) === String(id));
        const currentPartnerInfos = (partnerInfos && String(partnerInfos?.deceasedId) === String(id)) ? partnerInfos : null;
        const defaultValue = getDefaultThoughtsTo(deceasedInfos, currentChildrenInfos, currentPartnerInfos);
        if (defaultValue && defaultValue !== lastAutoFilledRef.current) {
            setThoughtsTo(defaultValue);
            setIsFormModified(true);
            lastAutoFilledRef.current = defaultValue;
        }
    }, [deceasedInfos, childrenInfos, partnerInfos, thoughtsTo, id]);

    const handleSubmitThoughts = useCallback(async (e) => {
        e?.preventDefault?.();
        removeErrorList();

        let body = {
            deceasedId: id,
            thoughtsTo: thoughtsTo,
        }

        await updateEventThoutght(token, body)
            .then((response) => {
                if (!response?.response?.data?.errors) {
                    setIsFormModified(false);

                    // Afficher le message de succès dans le bon fieldset
                    const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                    if (errorDiv) {
                        errorDiv.insertAdjacentHTML('beforeend', '<p class="success-message">Les modifications ont été enregistrées avec succès !</p>');

                        // Supprimer le message après 3 secondes
                        setTimeout(() => {
                            const successMsg = errorDiv.querySelector('.success-message');
                            if (successMsg) {
                                successMsg.remove();
                            }
                        }, 3000);
                    }

                    if (apiEventAddedInfos) {
                        apiEventAddedInfos();
                    }
                    if (apiDeceasedInfos) {
                        apiDeceasedInfos();
                    }
                } else {
                    getErrorListFromValidation(response?.response?.data?.errors)
                }
            })
    }, [thoughtsTo, token, id, apiEventAddedInfos, apiDeceasedInfos]);

    useEffect(() => {
        if (registerSubmitFunction) {
            registerSubmitFunction(handleSubmitThoughts);
        }
    }, [registerSubmitFunction, handleSubmitThoughts]);

    const handleChange = (e) => {
        setThoughtsTo(e.target.value);
        setIsFormModified(true);
    }

    return (
        <fieldset ref={fieldsetRef}>
            <legend>Message personnalisé pour l'avis de décès</legend>
            {/*<div className="form-input">
                <label htmlFor="startPhrase">Phrase de début pour l'avis de décès :</label>
                <select ref={startPhraseRef} name="startPhrase" value={startPhrase} onChange={onChangeStartPhrase}>
                    <option disabled={true} value={0}>-- Choisissez une formule --</option>
                    {
                        startPhraseList?.map((phrase) => (
                            <option name="startPhrase" key={phrase.id} value={phrase.id}>{phrase.startPhrase}</option>
                        ))
                    }
                </select>
            </div>*/}
            {/*{startPhrase !== 0 &&
                <div className="form-input html-textarea">
                    <label htmlFor="customStartPhrase">Phrase de début :</label>
                    <textarea
                        name="customStartPhrase"
                        className="thoughts-textarea"
                        value={customStartPhrase}
                        placeholder="Rédigez votre propre phrase de début..."
                        onChange={onChangeCustomStartPhrase}
                    />
                </div>
            }*/}
            <div className="form-input html-textarea">
                <label htmlFor="thoughtsTo">Tout notre soutien à :</label>
                <textarea 
                    name="thoughtsTo"
                    className="thoughts-textarea"
                    value={thoughtsTo} 
                    placeholder="Tout notre soutien à"
                    onChange={handleChange}
                />
            </div>
            <div className="errors"></div>
            
            {!noSubmitButton && isFormModified && (
                <div className="buttons-container">
                    <button type="button" className="green-button" onClick={handleSubmitThoughts}>
                        <i className="fa fa-check"/><span>Enregistrer les modifications</span>
                    </button>
                </div>
            )}
        </fieldset>
    )
}